import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";

import forexImg from "../../img/markets/forex-chart.webp";
import indicesImg from "../../img/markets/ch2.webp";
import commoditiesImg from "../../img/markets/ch3.webp";
import stocksImg from "../../img/markets/ch4.webp";
import cryptoImg from "../../img/markets/ch5.webp";

const MARKETS = [
  { numKey: "markets.oneaccount.m1.num", nameKey: "markets.oneaccount.m1.name", descKey: "markets.oneaccount.m1.desc", img: forexImg },
  { numKey: "markets.oneaccount.m2.num", nameKey: "markets.oneaccount.m2.name", descKey: "markets.oneaccount.m2.desc", img: indicesImg },
  { numKey: "markets.oneaccount.m3.num", nameKey: "markets.oneaccount.m3.name", descKey: "markets.oneaccount.m3.desc", img: commoditiesImg },
  { numKey: "markets.oneaccount.m4.num", nameKey: "markets.oneaccount.m4.name", descKey: "markets.oneaccount.m4.desc", img: stocksImg },
  { numKey: "markets.oneaccount.m5.num", nameKey: "markets.oneaccount.m5.name", descKey: "markets.oneaccount.m5.desc", img: cryptoImg },
];

const getCanHover = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

function OneAccount() {
  const { t } = useTranslation();
  const [canHover, setCanHover] = useState(getCanHover);
  const [active, setActive] = useState(() => (getCanHover() ? 0 : null));

  useEffect(() => {
    const media = window.matchMedia("(hover: hover) and (pointer: fine)");
    const handleChange = () => {
      setCanHover(media.matches);
      setActive(media.matches ? 0 : null);
    };

    handleChange();

    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
      return () => media.removeEventListener("change", handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    MARKETS.forEach((market) => {
      const image = new Image();
      image.src = market.img;
    });
  }, []);

  return (
    <section className="mt">
      {/* Header */}
      <div className="glcontainer flex flex-col xl:flex-row xl:items-start xl:justify-between gap-[20px] mb-[40px] xl:mb-[60px]">
        <div className="flex flex-col font-['Montserrat'] font-black text-[24px] xl:text-[46px] text-[#0a0a0a] tracking-[-0.92px] uppercase leading-none xl:max-w-[504px]">
          <span>{t("markets.oneaccount.title1")}</span>
          <span>{t("markets.oneaccount.title2")}</span>
        </div>
        <p className="font-['Inter'] font-normal text-[14px] xl:text-[18px] text-[#717171] leading-[1.4] xl:max-w-[600px]">
          {t("markets.oneaccount.desc")}
        </p>
      </div>

      {/* Accordion rows */}
      <div
        className="flex flex-col w-full cursor-my"
        onMouseLeave={() => canHover && setActive(0)}
      >
        {MARKETS.map((market, i) => {
          const isActive = i === active;
          return isActive ? (
            /* Expanded item */
            <div
              key={i}
              className="bg-[#0a0a0a] w-full"
              onMouseEnter={() => canHover && setActive(i)}
              onClick={() => !canHover && setActive(null)}
            >
              <div className="glcontainer py-[18px] xl:py-0 xl:h-[432px] flex flex-col xl:flex-row xl:items-center gap-[20px]">
                {/* Text */}
                <div className="xl:w-[600px] shrink-0 flex flex-col">
                  <span className="font-['Montserrat'] font-black text-[40px] xl:text-[96px] text-[#212121] leading-[1.2]">
                    {t(market.numKey)}
                  </span>
                  <div className="flex flex-col gap-[20px] xl:gap-[40px]">
                    <span className="font-['Montserrat'] font-black text-[32px] xl:text-[64px] text-white leading-[1.2]">
                      {t(market.nameKey)}
                    </span>
                    <p className="font-['Inter'] font-normal text-[14px] xl:text-[18px] text-[#717171] leading-[1.4] xl:max-w-[551px]">
                      {t(market.descKey)}
                    </p>
                  </div>
                </div>
                {/* Chart image */}
                <div className="w-full xl:flex-1 h-[220px] xl:h-full overflow-hidden shrink-0">
                  <img
                    src={market.img}
                    alt=""
                    loading="eager"
                    decoding="sync"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Collapsed item */
            <div
              key={i}
              onMouseEnter={() => canHover && setActive(i)}
              onClick={() => !canHover && setActive(i)}
              className="bg-[#f7f7f7] hover:bg-[#ececec] transition-colors duration-200 border-b border-[rgba(0,0,0,0.1)] w-full cursor-pointer"
            >
              <div className="glcontainer flex items-center justify-between py-[20px] xl:h-[127px] xl:py-0">
                <div className="flex items-center gap-[20px] xl:gap-[80px]">
                  <span className="font-['Montserrat'] font-normal text-[20px] xl:text-[32px] text-[#707070] leading-[1.4] w-[27px] xl:w-[44px] shrink-0">
                    {t(market.numKey)}
                  </span>
                  <span className="font-['Montserrat'] font-bold text-[24px] xl:text-[40px] text-[#020202] leading-normal">
                    {t(market.nameKey)}
                  </span>
                </div>
                <div className="rounded-full border border-[rgba(0,0,0,0.1)] bg-white flex items-center justify-center size-[40px] xl:size-[56px] shrink-0">
                  <ChevronRight size={18} className="text-[#020202]" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default OneAccount;
